import { first, get } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';
import { ChildProps, MutationFn } from 'react-apollo';
import classNames from 'classnames';

import { convertCurrency } from '@reverbdotcom/commons/src/convert_currency';
import {
  connectEditOfferFormQuery,
  connectUpdateNegotiationMutation,
} from '@reverbdotcom/commons/src/offers/offer_operations';
import NegotiationSummary from './negotiation_summary';
import UpdateOfferFormFields from './update_offer_form_fields';
import URLHelpers from '../shared/url_helpers';
import * as elog from '@reverbdotcom/commons/src/elog';

import {
  CommonsOffersEditOfferForm,
  CommonsOffersUpdateOfferForm,
  Input_core_apimessages_UpdateNegotiationRequest,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
} from '../../schemas/rql';

import {
  IUserContext,
  withUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';
import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import {
  offerFormReducer,
  emptyOfferFormState,
  ISubmittedOffer,
} from '@reverbdotcom/commons/src/offers/create_offer_modal';
import { OfferListingSidebar } from '@reverbdotcom/commons/src/offers/offer_listing_sidebar';
import { connect } from '@reverbdotcom/commons/src/connect';
import { OfferModalHeaderContent } from '@reverbdotcom/commons/src/offers/offer_modal_header_content';
import { priceRecommendationCanBeShown } from '@reverbdotcom/commons/src/offers/estimated_value_alert';

import {
  lastOffer as getLastOffer,
  lastOfferPrices as getLastOfferPrices,
  lastOfferPricesFor,
  activeNegotiation,
  offerShippingPriceAmount,
  offerQuantity,
  userCurrency as getUserCurrency,
  originalCurrency,
} from './negotiation_getters';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';

interface IExternalProps {
  listingId: string;
  isSeller: boolean;
  negotiationId: string;
  onSuccess: (requestingParty: NegotiationParty) => void;
  isDialogOpen?: boolean;
  onRequestDialogClose?: () => void;
  negotiationAction: NegotiationAction;
  useDesignChanges?: boolean;
}

interface ApolloProps {
  updateNegotiation: MutationFn<CommonsOffersUpdateOfferForm.Mutation, CommonsOffersUpdateOfferForm.Variables>;
}

type IProps = ChildProps<IExternalProps, CommonsOffersEditOfferForm.Query> & ApolloProps & IUserContext;

const TRACKING_COMPONENT = 'UpdateOfferModal';

interface BaseOffer {
  price: string;
  shippingPrice: string;
  quantity: number;
  message: string;
}

function OfferSidebar({ listing, loading, isSeller }) {
  return (
    <OfferListingSidebar
      listing={listing}
      isLoading={loading}
      isSeller={isSeller}
      listingUrlGenerator={URLHelpers.listingPagePath}
    />
  );
}

function ErrorDisplay() {
  return (
    <RCAlertBox type="error">
      <p className="mb-0">
        {I18n.t('commons.offers.error')}
      </p>
    </RCAlertBox>
  );
}

function LoadingDisplay() {
  return (
    <div className="offers-form offers-form--loading">
      <div className="offers-form-placeholder" />
      <div className="offers-form-placeholder" />
      <div className="offers-form-placeholder" />
    </div>
  );
}

export function UpdateOfferModalDialog({
  isDialogOpen,
  onRequestDialogClose,
  negotiationAction,
  isSeller,
  updateNegotiation,
  onSuccess,
  listingId,
  negotiationId,
  data,
  user,
  useDesignChanges,
}: IProps) {
  const [formState, dispatch] = React.useReducer(offerFormReducer, emptyOfferFormState);
  const [isFormSaving, setIsFormSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [acceptedOrCounteredOffer, setAcceptedOrCounteredOffer] = React.useState<ISubmittedOffer>({ price: '', shippingPrice: '', totalPrice: '' });

  const { loading, error, listing } = data || {};

  const negotiation = activeNegotiation(data?.me);
  const lastOffer = getLastOffer(negotiation);
  const userCurrency = getUserCurrency(data, user);
  const shopCurrency = listing?.shop?.currency;
  const exchangeRates = data?.exchangeRates?.rates || [];
  const party = isSeller ? NegotiationParty.SELLER : NegotiationParty.BUYER;
  const lastOfferPrices = getLastOfferPrices(data?.me);
  const originalLastOfferPrices = lastOfferPricesFor(NegotiationParty.SELLER, negotiation);
  const isCounterOffer = negotiationAction === NegotiationAction.COUNTER;
  const closeDialog = () => {
    if (onRequestDialogClose) {
      onRequestDialogClose();
    }
  };

  const missingExchangeRates = !exchangeRates.length &&
    !isSeller &&
    (userCurrency !== originalCurrency(data));
  const showError = error || (!negotiation && !loading) || missingExchangeRates;
  const submitDisabled = loading || isFormSaving || (isCounterOffer && !formState.price);

  const showPriceRecommendation = priceRecommendationCanBeShown(user, shopCurrency, listing);
  const hasAcceptedOrCounteredOffer = !!acceptedOrCounteredOffer?.totalPrice;

  React.useEffect(() => {
    if (loading || !negotiation) { return null; }

    const initialShippingPrice = offerShippingPriceAmount(
      formState.shippingPrice || undefined, // the default shipping price behavior kicks in when the value is undefined, not the empty string
      data?.me,
    );
    dispatch({ type: 'setShippingPrice', value: initialShippingPrice });

    const initialQuantity = offerQuantity(lastOffer, formState.quantity);
    dispatch({ type: 'setQuantity', value: initialQuantity });
  }, [negotiation, loading]);

  function modalHeaderTitle() {
    if (useDesignChanges && hasAcceptedOrCounteredOffer) {
      if (negotiationAction == NegotiationAction.ACCEPT) {
        return I18n.t('discovery.offers.confirmation.modalHeader.accepted');
      } else if (negotiationAction == NegotiationAction.COUNTER) {
        return I18n.t('discovery.offers.confirmation.modalHeader.countered');
      }
    }
    return I18n.t(`discovery.offers.headerTitle.${negotiationAction.toLocaleLowerCase()}`);
  }

  function saveButtonText() {
    switch (negotiationAction) {
      case NegotiationAction.ACCEPT:
        return I18n.t('discovery.offers.headerTitle.accept');
      case NegotiationAction.REJECT:
        return I18n.t('discovery.offers.headerTitle.reject');
      case NegotiationAction.COUNTER:
      default:
        return I18n.t('discovery.offers.submitButton.counter.label');
    }
  }

  function existingOffer(): BaseOffer {
    return {
      price: lastOffer.prices.price.display.amount,
      shippingPrice: lastOffer.prices.shippingPrice.display.amount,
      quantity: first(lastOffer.offerItems).quantity,
      message: formState.message,
    };
  }

  function offerToSubmitOnUpdate(): Input_core_apimessages_UpdateNegotiationRequest {
    const baseOffer = isCounterOffer ? formState : existingOffer();

    const shopCurrencyOfferPrice = convertCurrency(
      userCurrency,
      originalCurrency(data),
      exchangeRates,
      baseOffer.price,
    );
    const shopCurrencyOfferShippingPrice = convertCurrency(
      userCurrency,
      originalCurrency(data),
      exchangeRates,
      baseOffer.shippingPrice,
    );

    return {
      action: negotiationAction,
      id: negotiation.id,
      message: formState.message,
      offerItems: [
        {
          listingId: data.listing.id,
          quantity: baseOffer.quantity,
          price: shopCurrencyOfferPrice,
          shippingPrice: shopCurrencyOfferShippingPrice,
        },
      ],
    };
  }

  function trackingContext() {
    return {
      listingId: listingId,
      negotiationId: negotiationId,
      shopCurrency: listing.shop.currency,
      userCurrency: userCurrency,
    };
  }

  async function doNegotiationUpdate(): Promise<CommonsOffersUpdateOfferForm.Negotiation> {
    const input = offerToSubmitOnUpdate();
    const result = await updateNegotiation({ variables: { input } });
    return get(result, 'data.updateNegotiation.negotiation');
  }

  async function handleSubmit() {
    if (hasAcceptedOrCounteredOffer) {
      onSuccess(party);
      closeDialog();
    } else {
      try {
        setIsFormSaving(true);
        setErrorMessage('');

        const response = await doNegotiationUpdate();

        setIsFormSaving(false);

        if (useDesignChanges && negotiationAction !== NegotiationAction.REJECT) {
          const { prices } = response.lastOffer;
          setAcceptedOrCounteredOffer({
            price: prices.price.display.display,
            shippingPrice: prices.shippingPrice.display.display,
            totalPrice: prices.totalPrice.display.display,
          });
        } else {
          onSuccess(party);
          closeDialog();
        }
      } catch (errors) {
        setIsFormSaving(false);
        setErrorMessage(I18n.t('discovery.offers.updateOfferSubmitError'));

        const { message: graphQLErrorMessage } = first(get(errors, 'graphQLErrors', [{ message: '' }]));
        elog.error(
          TRACKING_COMPONENT,
          {
            ...trackingContext(),
            errors,
          },
          new Error(graphQLErrorMessage),
        );
      }
    }
  }

  function handleClose() {
    if (hasAcceptedOrCounteredOffer) {
      onSuccess(party);
    }
    closeDialog();
  }

  if (!data) {
    return null;
  }

  return (
    <ModalDialog
      isOpen={isDialogOpen}
      onRequestClose={handleClose}
      headerTitle={modalHeaderTitle()}
      sidebarContent={!useDesignChanges && (
        <OfferSidebar
          listing={listing}
          loading={loading}
          isSeller={isSeller}
        />
      )}
      onSubmit={handleSubmit}
      saveButtonText={hasAcceptedOrCounteredOffer ? I18n.t('discovery.offers.confirmation.buttonText') : saveButtonText()}
      isDisabled={submitDisabled}
      hideFooterDismissButton={hasAcceptedOrCounteredOffer}
    >
      {showError && <ErrorDisplay/>}

      {loading && !listing && <LoadingDisplay/>}

      {!loading && !error && listing && negotiation && (
        <>
          {useDesignChanges && (
            <OfferModalHeaderContent
              listing={listing}
              isSeller={isSeller}
              refetch={data.refetch}
              updateMode={true}
              submittedOffer={hasAcceptedOrCounteredOffer && acceptedOrCounteredOffer}
            />
          )}

          {!hasAcceptedOrCounteredOffer && (
            <>
              <div className={classNames(
                { 'offers-form__header': !useDesignChanges },
              )}>
                <NegotiationSummary
                  prices={lastOfferPrices}
                  originalPrices={originalLastOfferPrices}
                  listing={listing}
                  negotiation={negotiation}
                  party={party}
                  useDesignChanges={useDesignChanges}
                />
              </div>

              <UpdateOfferFormFields
                error={errorMessage}
                listing={listing}
                negotiation={negotiation}
                currency={userCurrency}
                exchangeRates={exchangeRates}
                offer={formState}
                existingOffer={existingOffer()}
                onOfferChange={dispatch}
                action={negotiationAction}
                party={party}
                modalDialogVersion={true}
                useDesignChanges={useDesignChanges}
                priceRecommendation={showPriceRecommendation && listing.priceRecommendation}
              />
            </>
          )}

          {hasAcceptedOrCounteredOffer && (
            <div className="offers-form__confirmation">
              {isSeller && (
                <>
                  <h4>{I18n.t('discovery.offers.confirmation.header')}</h4>
                  <ul>
                    <li>
                      {negotiationAction === NegotiationAction.ACCEPT && I18n.t('discovery.offers.confirmation.accepted.seller.line1')}
                      {negotiationAction === NegotiationAction.COUNTER && (
                        <SanitizedRender
                          html={I18n.t('discovery.offers.confirmation.countered.seller.line1')}
                        />
                      )}
                    </li>
                    <li>
                      {negotiationAction === NegotiationAction.ACCEPT && (
                        <SanitizedRender
                          html={I18n.t('discovery.offers.confirmation.accepted.seller.line2')}
                        />
                      )}
                      {negotiationAction === NegotiationAction.COUNTER && (
                        <SanitizedRender
                          html={I18n.t('discovery.offers.confirmation.countered.seller.line2')}
                        />
                      )}
                    </li>
                  </ul>
                </>
              )}
            </div>
          )}
        </>
      )}
    </ModalDialog>
  );
}

export default connect<IExternalProps>([
  connectEditOfferFormQuery,
  connectUpdateNegotiationMutation,
  withUserContext,
])(UpdateOfferModalDialog);
